import { useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';

import { useSideMenuContext } from 'components/shared/side-menu/side-menu-layout';
import { SIDEMENU_WIDTH } from 'components/shared/side-menu/side-menu.constants';
import { useFeatureHintTarget } from 'components/shared/feature-hints';

import composeRefs from 'helpers/compose-refs';

import { AiAssistantSessionProvider } from '../../context/ai-assistant-session-context';

import {
  useMarkPanelActions,
  useMarkPanelReturnFocus,
  useMarkPanelState,
} from '../mark-panel-context';
import {
  MARK_FLOATING_BUTTON_HINT_ID,
  MARK_PANEL_WIDTH_CLASS,
  MARK_PANEL_Z_INDEX_CLASS,
} from '../mark-panel.constants';

import MarkFloatingButton from './mark-floating-button';
import MarkNewChatConfirmation from './mark-new-chat-confirmation';
import MarkPanelChat from './mark-panel-chat';

const MarkPanelHost = () => {
  const state = useMarkPanelState();
  const actions = useMarkPanelActions();
  const { isCollapsed, setTemporarilyCollapsed } = useSideMenuContext();
  const returnFocusRef = useMarkPanelReturnFocus();

  const floatingButtonRef = useRef<HTMLButtonElement>(null);
  const shouldFocusFloatingButtonRef = useRef(false);
  const floatingButtonHint = useFeatureHintTarget(MARK_FLOATING_BUTTON_HINT_ID);
  const composedFloatingButtonRef = useMemo(
    () => composeRefs(floatingButtonRef, floatingButtonHint.ref),
    [floatingButtonHint.ref]
  );

  const status = state?.status ?? 'closed';

  useEffect(() => {
    setTemporarilyCollapsed(status === 'docked');

    return () => setTemporarilyCollapsed(false);
  }, [status, setTemporarilyCollapsed]);

  useEffect(() => {
    if (shouldFocusFloatingButtonRef.current && floatingButtonRef.current) {
      floatingButtonRef.current.focus();
      shouldFocusFloatingButtonRef.current = false;
    }
  });

  if (!state || !actions) return null;

  const handleClose = (): void => {
    const trigger = returnFocusRef?.current;

    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      shouldFocusFloatingButtonRef.current = true;
    }

    actions.close('closeButton');
  };

  if (state.activeSurface && status === 'closed') {
    const leftClass = isCollapsed
      ? SIDEMENU_WIDTH.collapsedLeft
      : SIDEMENU_WIDTH.expandedLeft;

    return (
      <>
        <div
          className={classNames(
            'pointer-events-none fixed inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/70 to-white/0',
            MARK_PANEL_Z_INDEX_CLASS,
            leftClass
          )}
        />

        <div
          className={classNames(
            'pointer-events-none fixed bottom-5 right-0 flex justify-center animate-mark-floating-in motion-reduce:animate-none',
            MARK_PANEL_Z_INDEX_CLASS,
            leftClass
          )}
        >
          <MarkFloatingButton
            ref={composedFloatingButtonRef}
            onClick={actions.openPageContext}
            className={twMerge(
              'pointer-events-auto',
              floatingButtonHint.className
            )}
            data-feature-hint-target={MARK_FLOATING_BUTTON_HINT_ID}
          />
        </div>
      </>
    );
  }

  if (status === 'docked' && state.session) {
    return (
      <div
        className={classNames(
          'h-full overflow-hidden animate-mark-panel-in motion-reduce:animate-none',
          MARK_PANEL_WIDTH_CLASS
        )}
      >
        <AiAssistantSessionProvider>
          <MarkPanelChat
            key={state.session.key}
            session={state.session}
            onClose={handleClose}
          />
        </AiAssistantSessionProvider>

        {state.pendingOpen && (
          <MarkNewChatConfirmation
            key={state.pendingOpen.context.key}
            anchorElement={state.pendingOpen.anchorElement}
            onConfirm={actions.confirmPendingOpen}
            onDismiss={actions.dismissPendingOpen}
          />
        )}
      </div>
    );
  }

  return null;
};

export default MarkPanelHost;
